/**
 * G-Admin back-office client (`/api/admin/*`, PRD §4.5).
 */

import { useMutation, useQuery, useQueryClient, type QueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { api, saveBlob } from "@/lib/api";
import { exportProjectYearMatrix } from "@/lib/export-xlsx";
import { useSession } from "@/lib/session";
import type { Lifecycle } from "@/lib/tenancy";
import type { LiveOverview } from "@/shared/schemas/tracking";

export type MatrixStatus = "full" | "partial" | "empty";

export interface AdminUser {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  role: string;
  companyId: string | null;
  companyName: string | null;
  isEnabled: boolean;
  archived: boolean;
  weeklyHours: number | null;
  lastLoginAt: string | null;
  lastEntryAt: string | null;
  createdAt: string;
}

export interface AdminProject {
  id: string;
  name: string;
  companyId: string;
  companyName: string;
  bundesland: string | null;
  startDate: string;
  endDate: string;
  archived: boolean;
  personCount: number;
}

export interface MatrixCell {
  month: number;
  hours: number;
  expected: number;
  status: MatrixStatus;
}

export interface MatrixEmployee {
  userId: string;
  firstName: string;
  lastName: string;
  email: string;
  position: string | null;
  months: MatrixCell[];
}

export interface MonthMatrix {
  projectId: string;
  projectName: string;
  year: number;
  employees: MatrixEmployee[];
}

export interface AdminWeekNote {
  id: string;
  userId: string;
  firstName: string;
  lastName: string;
  email: string;
  year: number;
  isoWeek: number;
  note: string;
  updatedAt: string;
  attachments: {
    id: string;
    filename: string;
    byteSize: number;
    createdAt: string;
  }[];
}

export interface EmailLogEntry {
  id: string;
  toEmail: string;
  template: string;
  status: string;
  error: string | null;
  meta: unknown;
  createdAt: string;
}

export interface EmailOutboxStats {
  queued: number;
  failed: number;
  sending: number;
  dailySent24h: number;
  dailyGuardPaused: boolean;
  dailyNormalCap: number;
}

export interface EmailLogResponse {
  outbox: EmailOutboxStats;
  entries: EmailLogEntry[];
}

export interface AuditEventRow {
  id: string;
  eventType: string;
  actorUserId: string | null;
  actorEmail: string | null;
  actorName: string | null;
  target: string | null;
  targetResolved: string | null;
  targetRaw: string | null;
  targetEmail: string | null;
  reason: string | null;
  method: string | null;
  from: string | null;
  to: string | null;
  delivery: string | null;
  meta: unknown;
  metaRemainder: Record<string, unknown>;
  ip: string | null;
  createdAt: string;
}

export interface EntriesAuditRow {
  id: string;
  source: string;
  sourceId: string;
  action: string;
  oldRow: Record<string, unknown> | null;
  newRow: Record<string, unknown> | null;
  changedBy: string | null;
  changedByEmail: string | null;
  changedByName: string | null;
  changedByRole: string | null;
  changedVia: string | null;
  changeReason: string | null;
  changedAt: string;
  employeeId: string | null;
  employeeEmail: string | null;
  employeeName: string | null;
  entryDate: string | null;
}

export interface ChainVerification {
  rowCount: number;
  ok: boolean;
  mismatch: {
    id: string;
    source: string;
    source_id: string;
    reason: string;
  } | null;
}

export interface AppSettingRow {
  key: string;
  value: unknown;
  updatedAt: string | null;
}

export interface ImportBatchRow {
  id: string;
  kind: string;
  companyId: string;
  companyName: string;
  projectId: string | null;
  projectName: string | null;
  filename: string;
  dryRun: boolean;
  stats: unknown;
  createdBy: string | null;
  createdAt: string;
}

export interface CapacityBucket {
  key: string;
  label: string;
  rdHours: number;
  cap: number;
  pct: number;
  over: boolean;
  byProject: { projectId: string; projectName: string; rdHours: number }[];
}

export interface CapacityPerson {
  userId: string;
  firstName: string;
  lastName: string;
  email: string;
  weeklyHours: number;
  workdaysPerWeek: number;
  dailyCap: number;
  yearPct: number;
  yearOver: boolean;
  buckets: CapacityBucket[];
  overbookedDays: {
    date: string;
    rdHours: number;
    dailyCap: number;
    byProject: { projectId: string; projectName: string; rdHours: number }[];
  }[];
  /** Hard breaches: Σ R&D above the admin write ceiling, unlike `overbookedDays`. */
  overLimitDays: {
    date: string;
    rdHours: number;
    dailyCap: number;
    byProject: { projectId: string; projectName: string; rdHours: number }[];
  }[];
}

export interface CompanyCapacity {
  companyId: string;
  companyName: string;
  year: number;
  granularity: "week" | "month" | "year";
  people: CapacityPerson[];
}

export interface ReminderSettings {
  projectId: string;
  weekday: number;
  hour: number;
  active: boolean;
  subject: string | null;
  body: string | null;
  inviteSubject: string | null;
  inviteBody: string | null;
}

export interface ReminderMissing {
  userId: string;
  firstName: string;
  lastName: string;
  email: string;
}

export interface ReminderAssignee extends ReminderMissing {
  missing: boolean;
}

export interface ReminderLogEntry {
  id: string;
  userId: string;
  email: string;
  firstName: string;
  lastName: string;
  status: string;
  /** `"invite"` when meta.kind was invite; otherwise `"reminder"` (incl. legacy rows). */
  kind: "reminder" | "invite";
  createdAt: string;
}

export const adminKeys = {
  users: (includeArchived = false) => ["admin", "users", { includeArchived }] as const,
  projects: (includeArchived = false) => ["admin", "projects", { includeArchived }] as const,
  matrix: (projectId: string, year: number) => ["admin", "matrix", projectId, year] as const,
  weekNotes: (projectId: string, year: number) => ["admin", "weekNotes", projectId, year] as const,
  emailLog: (filters?: { template?: string; recipient?: string }) =>
    ["admin", "emailLog", filters ?? {}] as const,
  auditEvents: (
    page = 1,
    pageSize = 50,
    filters: { q?: string; eventType?: string; from?: string; to?: string } = {},
  ) => ["admin", "auditEvents", { page, pageSize, ...filters }] as const,
  entriesAudit: (projectId: string, page = 1, pageSize = 50) =>
    ["admin", "entriesAudit", projectId, { page, pageSize }] as const,
  auditChain: () => ["admin", "auditChain"] as const,
  settings: () => ["admin", "settings"] as const,
  importBatches: () => ["admin", "importBatches"] as const,
  capacity: (companyId: string, year: number, granularity: string) =>
    ["admin", "capacity", companyId, year, granularity] as const,
  reminders: (projectId: string) => ["admin", "reminders", projectId] as const,
};

function touchAdmin(client: QueryClient): void {
  void client.invalidateQueries({ queryKey: ["admin"] });
}

function query(params: Record<string, string | number | boolean | undefined>): string {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === false) continue;
    search.set(key, String(value));
  }
  const encoded = search.toString();
  return encoded ? `?${encoded}` : "";
}

function useAdminMutation<TInput, TResult>(run: (input: TInput) => Promise<TResult>) {
  const client = useQueryClient();
  return useMutation({ mutationFn: run, onSuccess: () => touchAdmin(client) });
}

// --------------------------------------------------------------------- reads

export function useAdminUsers(options: { includeArchived?: boolean } = {}) {
  return useQuery({
    queryKey: adminKeys.users(options.includeArchived),
    queryFn: () =>
      api.get<{ users: AdminUser[] }>(
        `/admin/users${query({ includeArchived: options.includeArchived })}`,
      ),
    select: (data) => data.users,
  });
}

export function useAdminProjects(options: { includeArchived?: boolean } = {}) {
  return useQuery({
    queryKey: adminKeys.projects(options.includeArchived),
    queryFn: () =>
      api.get<{ projects: AdminProject[] }>(
        `/admin/projects${query({ includeArchived: options.includeArchived })}`,
      ),
    select: (data) => data.projects,
  });
}

export function useAdminMatrix(projectId: string | undefined, year: number) {
  return useQuery({
    queryKey: adminKeys.matrix(projectId ?? "none", year),
    queryFn: () =>
      api.get<{ matrix: MonthMatrix }>(`/admin/projects/${projectId}/matrix${query({ year })}`),
    enabled: Boolean(projectId),
    select: (data) => data.matrix,
  });
}

export function useAdminWeekNotes(projectId: string | undefined, year: number) {
  return useQuery({
    queryKey: adminKeys.weekNotes(projectId ?? "none", year),
    queryFn: () =>
      api.get<{ entries: AdminWeekNote[] }>(
        `/admin/projects/${projectId}/week-notes${query({ year })}`,
      ),
    enabled: Boolean(projectId),
    select: (data) => data.entries,
  });
}

export function useAdminEmailLog(filters: { template?: string; recipient?: string } = {}) {
  const template = filters.template || undefined;
  const recipient = filters.recipient?.trim() || undefined;
  return useQuery({
    queryKey: adminKeys.emailLog({ template, recipient }),
    queryFn: () => api.get<EmailLogResponse>(`/admin/email-log${query({ template, recipient })}`),
  });
}

export interface AdminAuditEventsPage {
  events: AuditEventRow[];
  total: number;
  page: number;
  pageSize: number;
}

export interface AdminEntriesAuditPage {
  entries: EntriesAuditRow[];
  total: number;
  page: number;
  pageSize: number;
}

export function useAdminAuditEvents(
  options: {
    page?: number;
    pageSize?: number;
    q?: string;
    eventType?: string;
    from?: string;
    to?: string;
  } = {},
) {
  const page = options.page ?? 1;
  const pageSize = options.pageSize ?? 50;
  const q = options.q?.trim() || undefined;
  const eventType = options.eventType?.trim() || undefined;
  const from = options.from?.trim() || undefined;
  const to = options.to?.trim() || undefined;
  return useQuery({
    queryKey: adminKeys.auditEvents(page, pageSize, { q, eventType, from, to }),
    queryFn: () =>
      api.get<AdminAuditEventsPage>(
        `/admin/audit-events${query({ page, pageSize, q, eventType, from, to })}`,
      ),
  });
}

export function useAdminEntriesAudit(
  projectId: string | undefined,
  options: { page?: number; pageSize?: number } = {},
) {
  const page = options.page ?? 1;
  const pageSize = options.pageSize ?? 50;
  return useQuery({
    queryKey: adminKeys.entriesAudit(projectId ?? "none", page, pageSize),
    queryFn: () =>
      api.get<AdminEntriesAuditPage>(`/admin/entries-audit${query({ projectId, page, pageSize })}`),
    enabled: Boolean(projectId),
  });
}

export function useAdminAuditChain() {
  return useQuery({
    queryKey: adminKeys.auditChain(),
    queryFn: () => api.get<{ chain: ChainVerification }>("/admin/audit-chain"),
    select: (data) => data.chain,
  });
}

export function useAdminSettings() {
  return useQuery({
    queryKey: adminKeys.settings(),
    queryFn: () => api.get<{ settings: AppSettingRow[] }>("/admin/settings"),
    select: (data) => data.settings,
  });
}

export function useAdminImportBatches() {
  return useQuery({
    queryKey: adminKeys.importBatches(),
    queryFn: () => api.get<{ batches: ImportBatchRow[] }>("/admin/import-batches"),
    select: (data) => data.batches,
  });
}

export function useCompanyCapacity(
  companyId: string | undefined,
  year: number,
  granularity: "week" | "month" | "year",
) {
  return useQuery({
    queryKey: adminKeys.capacity(companyId ?? "none", year, granularity),
    queryFn: () =>
      api.get<{ capacity: CompanyCapacity }>(
        `/admin/companies/${companyId}/capacity${query({ year, granularity })}`,
      ),
    enabled: Boolean(companyId),
    select: (data) => data.capacity,
  });
}

export function useProjectReminders(projectId: string | undefined) {
  return useQuery({
    queryKey: adminKeys.reminders(projectId ?? "none"),
    queryFn: () =>
      api.get<{
        settings: ReminderSettings;
        senderEmail: string | null;
        projectName: string;
        assignees: ReminderAssignee[];
        missing: ReminderMissing[];
        year: number;
        month: number;
        log: ReminderLogEntry[];
      }>(`/admin/projects/${projectId}/reminders`),
    enabled: Boolean(projectId),
  });
}

// --------------------------------------------------------------------- writes

export function useAdminMagicLink() {
  return useAdminMutation((userId: string) => api.post<void>(`/admin/users/${userId}/magic-link`));
}

export function useAdminPasswordReset() {
  return useAdminMutation((userId: string) =>
    api.post<void>(`/admin/users/${userId}/password-reset`),
  );
}

export function useAdminSetUserEnabled() {
  return useAdminMutation((input: { id: string; isEnabled: boolean }) =>
    api.patch(`/users/${input.id}`, { isEnabled: input.isEnabled }),
  );
}

export function useAdminProjectLifecycle() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async (input: { id: string; action: Lifecycle }) => {
      if (input.action === "delete") return api.delete<void>(`/projects/${input.id}`);
      await api.post(`/projects/${input.id}/${input.action}`);
    },
    onSuccess: () => {
      touchAdmin(client);
      void client.invalidateQueries({ queryKey: ["projects"] });
    },
  });
}

export function useAdminUpdateSetting() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (input: { key: string; value: unknown }) =>
      api.put<{ key: string; value: unknown }>(`/admin/settings/${input.key}`, {
        value: input.value,
      }),
    onSuccess: () => {
      touchAdmin(client);
      void client.invalidateQueries({ queryKey: ["work-profile-limits"] });
      void client.invalidateQueries({ queryKey: ["legal-documents"] });
    },
  });
}

export function useAdminSaveReminders(projectId: string) {
  return useAdminMutation(
    (input: {
      weekday: number;
      hour: number;
      active: boolean;
      subject?: string | null;
      body?: string | null;
      inviteSubject?: string | null;
      inviteBody?: string | null;
    }) => api.put<{ settings: ReminderSettings }>(`/admin/projects/${projectId}/reminders`, input),
  );
}

export function useAdminSendReminders(projectId: string) {
  return useAdminMutation((input?: { userIds?: string[]; locale?: "de" | "en" }) =>
    api.post<{ sent: number; results: { userId: string; status: string }[] }>(
      `/admin/projects/${projectId}/reminders/send`,
      input ?? {},
    ),
  );
}

export function useAdminSendInvites(projectId: string) {
  return useAdminMutation((input?: { userIds?: string[]; locale?: "de" | "en" }) =>
    api.post<{ sent: number; results: { userId: string; status: string }[] }>(
      `/admin/projects/${projectId}/invites/send`,
      input ?? {},
    ),
  );
}

export function useAdminCopyInviteLink(projectId: string) {
  return useAdminMutation((userId: string) =>
    api.post<{ url: string }>(`/admin/projects/${projectId}/invites/copy-link`, { userId }),
  );
}

/** Live-Tracking XLSX — same client builder as project Live (actual tracked data only). */
export function useDownloadAdminMatrixXlsx() {
  const { user } = useSession();
  const { t } = useTranslation();
  return useMutation({
    mutationFn: async (input: { projectId: string; year: number }) => {
      if (!user) throw new Error("Not signed in");
      const { overview } = await api.get<{ overview: LiveOverview }>(
        `/projects/${input.projectId}/live${query({ year: input.year })}`,
      );
      const filename = exportProjectYearMatrix(overview, {
        grantonomy_admin: t("appShell.role.grantonomy_admin"),
        company_admin: t("appShell.role.company_admin"),
        employee: t("appShell.role.employee"),
      });
      await api.post<void>("/exports/log", {
        type: "export_year_matrix",
        projectId: input.projectId,
        year: input.year,
        format: "xlsx",
      });
      return filename;
    },
  });
}

export function useDownloadAuditXlsx() {
  return useMutation({
    mutationFn: async (projectId: string) => {
      const { blob, filename } = await api.download(`/admin/audit.xlsx${query({ projectId })}`);
      saveBlob(blob, filename);
    },
  });
}

export function useDownloadAuditEventsCsv() {
  return useMutation({
    mutationFn: async () => {
      const { blob, filename } = await api.download("/admin/audit-events.csv");
      saveBlob(blob, filename);
    },
  });
}

export interface HoursImportPreview {
  filename: string;
  companyId: string;
  projectId: string | null;
  rows: {
    rowNumber: number;
    email: string;
    projectName: string | null;
    projectId: string | null;
    entryDate: string;
    totalHours: number;
    rdHours: number;
    absenceType: string | null;
    absenceFraction: number;
  }[];
  rejected: { rowNumber: number; email: string; entryDate: string | null; reason: string }[];
  people: {
    userId: string;
    email: string;
    firstName: string;
    lastName: string;
    months: {
      year: number;
      month: number;
      fileTotalHours: number;
      fileRdHours: number;
      dbTotalHours: number;
      dbRdHours: number;
      deltaTotal: number;
      deltaRd: number;
      rowCount: number;
    }[];
  }[];
  totals: {
    fileRows: number;
    accepted: number;
    rejected: number;
    fileTotalHours: number;
    fileRdHours: number;
  };
}

export function useDownloadHoursTemplate() {
  return useMutation({
    mutationFn: async (companyId: string) => {
      const { blob, filename } = await api.download(
        `/admin/companies/${companyId}/hours/import/template`,
      );
      saveBlob(blob, filename);
    },
  });
}

export function useHoursImportPreview() {
  return useMutation({
    mutationFn: (input: { companyId: string; projectId?: string | null; file: File }) =>
      api.upload<{ preview: HoursImportPreview }>(
        `/admin/companies/${input.companyId}/hours/import/preview${query({
          filename: input.file.name,
          projectId: input.projectId || undefined,
        })}`,
        input.file,
      ),
  });
}

export function useHoursImportCommit() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      companyId: string;
      projectId?: string | null;
      filename: string;
      rows: HoursImportPreview["rows"];
    }) =>
      api.post<{ batchId: string; stats: Record<string, number> }>(
        `/admin/companies/${input.companyId}/hours/import/commit`,
        {
          filename: input.filename,
          projectId: input.projectId ?? null,
          rows: input.rows,
        },
      ),
    onSuccess: () => touchAdmin(client),
  });
}
